import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Building, Mail, Clock, MapPin, Plus, Minus } from 'lucide-react';
import { db, auth } from '../../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// Default settings structure
const defaultSettings = {
  company_name: '',
  founding_year: new Date().getFullYear(),
  license_number: '',
  address: '',
  phone: '',
  email: '',
  business_hours: '',
  services: [''],
  certifications: [''],
  description: '',
  social_links: {
    instagram: '',
    kakao: '',
    naver: ''
  }
};

export default function AdminSettings() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(defaultSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication on component mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/admin/login');
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate]);

  // Fetch settings from Firebase
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        const settingsDoc = doc(db, 'settings', 'company');
        const docSnap = await getDoc(settingsDoc);
        
        if (docSnap.exists()) {
          setFormData({ ...defaultSettings, ...docSnap.data() });
        } else {
          // If no settings exist, use defaults
          setFormData(defaultSettings);
        }
      } catch (err) {
        console.error('Settings fetch failed:', err);
        setError('설정을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const settingsDoc = doc(db, 'settings', 'company');
      await setDoc(settingsDoc, formData);
      alert('설정이 성공적으로 저장되었습니다.');
    } catch (err) {
      console.error('Settings update failed:', err);
      alert('설정 저장 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateArrayField = (field, index, value) => {
    setFormData(prev => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => {
      const newArray = prev[field].filter((_, i) => i !== index);
      return { ...prev, [field]: newArray };
    });
  };

  const updateSocialLinks = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [platform]: value
      }
    }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <p className="text-red-600">설정을 불러오는 중 오류가 발생했습니다.</p>
      </div>
    );
  }

  const ArrayField = ({ label, field, data }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      {data.map((item, index) => (
        <div key={index} className="flex items-center mb-2">
          <input
            type="text"
            value={item}
            onChange={(e) => updateArrayField(field, index, e.target.value)}
            className="flex-1 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => removeArrayItem(field, index)}
            className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded-md"
          >
            <Minus className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        onClick={() => addArrayItem(field)}
        className="inline-flex items-center px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
      >
        <Plus className="w-4 h-4 mr-1" />
        추가
      </button>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">환경설정</h1>
          <p className="text-sm text-gray-600 mt-1">회사 정보와 운영 설정을 관리합니다</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 기본 정보 */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">기본 정보</h2>
          <div className="space-y-4">
            {/* 회사명 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                회사명 <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <Building className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => updateFormData('company_name', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            {/* 설립연도 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                설립연도 <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                value={formData.founding_year}
                onChange={(e) => updateFormData('founding_year', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* 사업자등록번호/면허번호 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                사업자등록번호/면허번호 <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                value={formData.license_number}
                onChange={(e) => updateFormData('license_number', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* 주소 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">주소</label>
              <div className="relative">
                <MapPin className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => updateFormData('address', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 전화번호 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">전화번호</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => updateFormData('phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 이메일 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 영업시간 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">영업시간</label>
              <div className="relative">
                <Clock className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={formData.business_hours}
                  onChange={(e) => updateFormData('business_hours', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* 서비스 및 자격증 */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">서비스 및 자격증</h2>
          <div className="space-y-6">
            <ArrayField label="제공 서비스" field="services" data={formData.services || []} />
            <ArrayField label="자격증 및 인증" field="certifications" data={formData.certifications || []} />
          </div>
        </div>

        {/* 설명 및 소셜 링크 */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">설명 및 소셜 링크</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">회사 소개</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => updateFormData('description', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">인스타그램</label>
                <input
                  type="text"
                  value={formData.social_links?.instagram || ''}
                  onChange={(e) => updateSocialLinks('instagram', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="@username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">카카오톡</label>
                <input
                  type="text"
                  value={formData.social_links?.kakao || ''}
                  onChange={(e) => updateSocialLinks('kakao', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="카카오톡 채널명"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">네이버 블로그</label>
                <input
                  type="text"
                  value={formData.social_links?.naver || ''}
                  onChange={(e) => updateSocialLinks('naver', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="블로그 URL"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}